import React, {RefObject, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@theme/makeStyles';
import {ERROR_CODES, getDrmFingerprint, sugarfansDrmSvg} from '@components/VideoPlayer/DRM/drmUtils';
import {useAppContext} from '@context/AppContext';
import CircularProgress from '@mui/material/CircularProgress';
import {reportError} from '../../../Utils/sentry';
import DrmError from '@components/VideoPlayer/DRM/DrmError';
import {FastEvoPlayer} from '@visiontsnpm/player';

const useStyles = makeStyles<string>()((theme, poster) => ({
    withPoster: {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundImage: poster ? `url(${poster})` : 'none'
    },
    drmContainer: {
        width: '100%',
        height: '100%',
        position: 'relative'
    }
}));

interface DrmVideoPlayerProps {
    token: string;
    isImage?: boolean;
    autoPlay?: boolean;
    load?: boolean;
    preload?: boolean;
    poster?: string;
    fileId?: string;
    playerRef?: RefObject<HTMLVideoElement>;
}

const DrmVideoPlayer2 = (props: DrmVideoPlayerProps): JSX.Element => {
    const {token, isImage = false, load = true, poster, fileId, playerRef: playerRefProps, autoPlay} = props;

    const {logger, controllers, setDrmAvailable} = useAppContext();

    const wrapperRef = useRef<HTMLDivElement>();
    const videoRef = playerRefProps || useRef<HTMLVideoElement>();

    const [initialized, setInitialized] = useState<boolean>(false);
    const [played, setPlayed] = useState<boolean>(false);

    const playerRef = useRef<FastEvoPlayer>(null);

    const {classes, cx} = useStyles(poster);

    // Log played event when no error
    useEffect(() => {
        if (played && fileId) {
            getDrmFingerprint()
                .then(fingerprint => controllers.drmListController.logError(fileId, fingerprint, false, null))
                .catch(() => {/* no-op */});
        }
    }, [played, fileId]);

    const errCallback = (err, detail) => {
        logger.debug(`[${token}] DRM player error`, err, detail);

        if (detail) {
            try {
                reportError(JSON.stringify(new DrmError(err, detail)), scope => scope.setTag('sf.boundary', 'drm'))
                    .catch(e => console.error(' > Error trying to report DRM error to Sentry', e));
            }
            catch (e) {
                console.error(' > Error trying to report DRM error to Sentry', e);
            }
        }

        if (fileId) {
            getDrmFingerprint()
                .then(fingerprint => controllers.drmListController.logError(fileId, fingerprint, true, err || null))
                .catch(() => {/* no-op */});
        }

        if (err === ERROR_CODES.SECURITY) {
            console.log(`[${token}] FastEvo security error -> enabling unsupported media component`);

            setDrmAvailable(false);
        }
    };

    const init = async() => {
        if (!(videoRef && videoRef.current)) {
            return;
        }

        console.log(' > [DRM] Initializing player');

        if (!isImage) {
            videoRef.current.setAttribute('displayControls', '');
        }

        playerRef.current = new FastEvoPlayer(token, videoRef.current, {
            isImage: isImage,
            disableBranding: true,
            customStyling: {
                stegoUi: {
                    customSVGForBadge: sugarfansDrmSvg
                }
            },
            errorCallback: errCallback
        });

        return playerRef.current.initiatePlayer();
    };

    const timeUpdatePlayHandler = () => setPlayed(true);

    useEffect(() => {
        if (!played && initialized && videoRef && videoRef.current) {
            console.log(' > Adding time update listener');
            videoRef.current.addEventListener('loadeddata', timeUpdatePlayHandler);
        }

        return () => {
            if (!played && initialized && videoRef && videoRef.current) {
                console.log(' > Removing time update listener');
                videoRef.current.removeEventListener('loadeddata', timeUpdatePlayHandler);
            }
        };
    }, [played, initialized]);

    useEffect(() => {
        if (load) {
            init()
                .then(() => setInitialized(true))
                .catch(err => console.error(' > [DRM] Error loading player', err));
        }

        return () => {
            if (playerRef && playerRef.current) {
                try {
                    console.log(' > [DRM] Destroying player...');
                    playerRef.current.destroy();
                }
                catch (e) {
                    console.log(' > [DRM] Error destroying player', e);
                }

                playerRef.current = null;
            }

            if (load && wrapperRef && wrapperRef.current) {
                setInitialized(false);
                setPlayed(false);
                wrapperRef.current.innerHTML = '';
            }
        };
    }, [load]);

    return <div ref={wrapperRef} className={cx(classes.drmContainer, poster ? classes.withPoster : undefined)}>
        {load && (
            <video
                ref={videoRef}
                // controls={!isImage}
                muted={isImage}
                playsInline
                style={{width: '100%', height: '100%'}}
                disablePictureInPicture
                autoPlay={autoPlay}
            />
        )}

        {load && !played && (<div className={'absoluteCenter'}><CircularProgress /></div>)}
    </div>;
};

export default DrmVideoPlayer2;